import React, { useEffect, useState } from "react";
import {
  useFindAssignmentsForStudentWithFilterLazyQuery,
  useGetAssignmentSubmitLazyQuery,
  useGetCampusQuery,
  useGetStudentByEmailIdQuery,
} from "../../../graphql/generated.tsx";
import { FaCalendarAlt, FaSearch } from "react-icons/fa";
import formatDate from "../../Admin/Helpers/formatDate.jsx";
import StudentLayout from "../Layout/index.jsx";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import LightSpinner from "../../../components/LightSpinner.jsx";
import NetworkIndicator from "../../Practice/Components/NetworkIndicator.jsx";

const ACCENTS = [
  { bar: "#8A5A2B", pillBg: "#F3E8DA", pillText: "#8A5A2B", pillBgHover: "#EAD9C3" },
  { bar: "#0F6E56", pillBg: "#E1F5EE", pillText: "#0F6E56", pillBgHover: "#C9EADF" },
  { bar: "#3C3489", pillBg: "#EEEDFE", pillText: "#3C3489", pillBgHover: "#E0DFFE" },
];

const getRowAccent = (globalIndex, rowSize = 3) => {
  const rowIndex = Math.floor(globalIndex / rowSize);
  const indexInRow = globalIndex % rowSize;
  const permutations = [
    [0, 1, 2],
    [1, 2, 0],
    [2, 0, 1],
    [0, 2, 1],
    [2, 1, 0],
    [1, 0, 2],
  ];
  const order = permutations[rowIndex % permutations.length];
  return ACCENTS[order[indexInRow] ?? 0];
};

const CalSVG = () => (
  <svg width="14" height="14" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
    <rect x="1.5" y="2.5" width="10" height="9" rx="2" stroke="currentColor" strokeWidth="1.2" />
    <path d="M4.5 1.5v2M8.5 1.5v2M1.5 5.5h10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const ArrowSVG = () => (
  <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
    <path d="M2.5 9.5L9.5 2.5M9.5 2.5H4.5M9.5 2.5V7.5" stroke="#667085" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const cardStyle = {
  background: "#fff",
  borderRadius: 16,
  overflow: "hidden",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  border: "1px solid #EAECF0",
  boxSizing: "border-box",
};

const AssignmentCard = ({ assignment, globalIndex, onNavigate }) => {
  const accent = getRowAccent(globalIndex, 3);
  const mildTopBar = `${accent.bar}99`;
  const [isHovered, setIsHovered] = useState(false);

  const dynamicCardStyle = {
    ...cardStyle,
    transform: "translateY(0)",
    boxShadow: "none",
    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
  };

  const arrowContainerStyle = {
    width: 27,
    height: 27,
    borderRadius: "50%",
    background: isHovered ? accent.pillBgHover : "#F9FAFB",
    border: "1px solid #EAECF0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "background 0.2s ease-in-out",
  };

  return (
    <div
      onClick={() => onNavigate(`/student/assignment-leaderboard/${assignment.id}`)}
      style={dynamicCardStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        style={{
          height: 7,
          background: mildTopBar,
          width: "100%",
          flexShrink: 0,
        }}
      />

      <div style={{ padding: "20px 18px 16px", display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <p style={{
            margin: 0,
            fontSize: 16,
            fontWeight: 600,
            color: "#101828",
            lineHeight: 1.45,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}>
            {assignment.name}
          </p>
        </div>

        <p style={{
          margin: 0,
          fontSize: 13,
          color: "#667085",
          lineHeight: 1.6,
          flex: 1,
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}>
          {assignment.short_description || "Take this assessment based on what you know and demonstrate your understanding."}
        </p>

        {assignment.domain ? (
          <span style={{
            alignSelf: "flex-start",
            fontSize: 12,
            fontWeight: 600,
            padding: "3px 9px",
            borderRadius: 8,
            letterSpacing: "0.03em",
            whiteSpace: "nowrap",
            background: accent.pillBg,
            color: accent.pillText,
          }}>
            {assignment.domain.toUpperCase()}
          </span>
        ) : null}
      </div>

      <div style={{
        padding: "12px 18px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderTop: "1px solid #F2F4F7",
        gap: 10,
        background: "linear-gradient(90deg, rgba(248,250,252,0.9), rgba(255,255,255,0.6))",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#667085", fontWeight: 500 }}>
          <CalSVG />
          {assignment.due_date ? formatDate(assignment.due_date) : "No due date"}
        </div>

        <div style={arrowContainerStyle}>
          <ArrowSVG />
        </div>
      </div>
    </div>
  );
};

const AssignmentLeaderboard = () => {
  const navigate = useNavigate();
  const email = JSON.parse(localStorage.getItem("user"))?.email;

  const { data: student, loading: studentLoading } = useGetStudentByEmailIdQuery({
    variables: { email },
  });
  const { data: campus } = useGetCampusQuery({
    variables: { id: student?.getStudentByEmailId?.campusId },
    skip: !student?.getStudentByEmailId,
  });

  const [filterType, setFilterType] = useState("week"); // all | week | month | custom
  const [customDateRange, setCustomDateRange] = useState({ startDate: null, endDate: null });
  const [showCalendar, setShowCalendar] = useState(false);
  const [activeField, setActiveField] = useState(null); // 'start' | 'end' | null

  const [getAssignments, { data: assignmentsData, loading: assignmentsLoading }] =
    useFindAssignmentsForStudentWithFilterLazyQuery();
  const [getAssignmentSubmit] = useGetAssignmentSubmitLazyQuery();

  const [completedAssignmentMap, setCompletedAssignmentMap] = useState({});
  const [isLoad, setIsLoad] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoad(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  // ✅ Fetch assignments when filter changes
  useEffect(() => {
    if (email) {
      if (filterType === "custom") {
        if (customDateRange.startDate && customDateRange.endDate) {
          getAssignments({
            variables: {
              email,
              filterType: "custom",
              customDateRange: {
                startDate: customDateRange.startDate,
                endDate: customDateRange.endDate,
              },
            },
          });
        }
      } else {
        getAssignments({
          variables: {
            email,
            filterType, // all | week | month
            customDateRange: null,
          },
        });
      }
    }
  }, [email, filterType, customDateRange, getAssignments]);
  
  // Removed assignments data debug log

  // ✅ Track completed assignments — wait for campus to be ready to avoid double-fire
  useEffect(() => {
    if (student && assignmentsData && campus?.campus?.name) {
      assignmentsData.findAssignmentsForStudentWithFilter.forEach((assignment) => {
        const vars = {
          variables: {
            assignmentId: assignment.id,
            studentId: student?.getStudentByEmailId.id,
            campus_name: campus?.campus.name,
          },
        };
        getAssignmentSubmit(vars).then((result) => {
          setCompletedAssignmentMap((prev) => ({
            ...prev,
            [assignment.id]: result?.data?.getAssignmentSubmit?.completed,
          }));
        });
      });
    }
  }, [student, assignmentsData, getAssignmentSubmit, campus]);

  // ✅ Choose assignments to display
  const filteredAssignments = assignmentsData?.findAssignmentsForStudentWithFilter || [];
  const displayedAssignments =
    filterType === "all"
      ? filteredAssignments
      : filteredAssignments.filter((assignment) => completedAssignmentMap[assignment.id]);
  // Enforce a minimum 2s loader window and also while queries are loading

  if (studentLoading || assignmentsLoading || isLoad) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center h-screen">
          <LightSpinner message="Loading assignments" />
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout backgroundClass="bg-white">
      <NetworkIndicator />
     
      <div className="p-5 lg:m-10 max-md:ml-4 bg-white">
        {/* Page Heading */}
        <div className="p-6 mb-5 space-y-4 text-center max-md:p-4">
          <h1 className="text-5xl font-extrabold max-md:text-4xl text-primary">
            🚀 Assignments Leaderboard
           
          </h1>
          <p className="text-lg text-gray-700">
            Compete with your peers and climb the leaderboard with every completed assignment!
          </p>
        </div>

        {/* 🔽 Filter Dropdown + Date Pickers */}
        <div className="flex flex-col items-center md:flex-row md:items-center md:justify-end mb-8 gap-3 md:gap-4">
          {filterType === "custom" && (
            <div className="flex flex-col items-center gap-2 md:flex-row md:items-center md:gap-3">
              {/* Start date input-like button */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setActiveField('start');
                    setShowCalendar((s) => activeField !== 'start' ? true : !s);
                  }}
                  className={`flex items-center gap-2 w-44 h-11 px-4 py-2 text-sm bg-white border rounded-2xl shadow-sm focus:outline-none transition-all
                    ${customDateRange.startDate ? 'border-primary' : 'border-gray-400'}
                    hover:shadow-md focus:ring-2 focus:ring-primary`}
                >
                  <FaCalendarAlt className="text-gray-500" />
                  <span className={`${customDateRange.startDate ? 'text-gray-800' : 'text-gray-400'}`}>
                    {customDateRange.startDate
                      ? new Date(customDateRange.startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                      : 'Start date'}
                  </span>
                </button>
                {showCalendar && activeField === 'start' && (
                  <div className="bt-datepicker-panel absolute left-0 z-40 p-4 mt-2 bg-white border rounded-xl shadow-xl w-[320px]">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-gray-700">Select start date</span>
                      <button
                        onClick={() => setShowCalendar(false)}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >✕</button>
                    </div>
                    <DatePicker
                      selected={customDateRange.startDate}
                      onChange={(date) => {
                        setCustomDateRange((prev) => ({ ...prev, startDate: date || null }));
                        setShowCalendar(false);
                      }}
                      inline
                      calendarClassName="bt-calendar"
                    />
                  </div>
                )}
              </div>

              {/* End date input-like button */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setActiveField('end');
                    setShowCalendar((s) => activeField !== 'end' ? true : !s);
                  }}
                  className={`flex items-center gap-2 w-44 h-11 px-4 py-2 text-sm bg-white border rounded-2xl shadow-sm focus:outline-none transition-all
                    ${customDateRange.endDate ? 'border-primary' : 'border-gray-400'}
                    hover:shadow-md focus:ring-2 focus:ring-primary`}
                >
                  <FaCalendarAlt className="text-gray-500" />
                  <span className={`${customDateRange.endDate ? 'text-gray-800' : 'text-gray-400'}`}>
                    {customDateRange.endDate
                      ? new Date(customDateRange.endDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                      : 'End date'}
                  </span>
                </button>
                {showCalendar && activeField === 'end' && (
                  <div className="bt-datepicker-panel absolute left-0 z-40 p-4 mt-2 bg-white border rounded-xl shadow-xl w-[320px]">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-gray-700">Select end date</span>
                      <button
                        onClick={() => setShowCalendar(false)}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >✕</button>
                    </div>
                    <DatePicker
                      selected={customDateRange.endDate}
                      onChange={(date) => {
                        setCustomDateRange((prev) => ({ ...prev, endDate: date || null }));
                        setShowCalendar(false);
                      }}
                      inline
                      calendarClassName="bt-calendar"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="p-2 text-gray-700 border rounded-md border-primary"
          >
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>

        {/* 📋 Assignments List */}
        {displayedAssignments.length > 0 ? (
          <div className="grid grid-cols-1 gap-x-4 gap-y-6 md:grid-cols-2 lg:grid-cols-3">
            {displayedAssignments.map((assignment, index) => (
              <AssignmentCard
                key={assignment.id}
                assignment={assignment}
                globalIndex={index}
                onNavigate={navigate}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-[500px] text-center">
            <FaSearch className="mb-4 text-gray-400 text-7xl" />
            <h2 className="mb-2 text-2xl font-bold text-gray-600">
              Oops, No Results Found
            </h2>
            <p className="text-gray-500 mb-6 lg:w-[500px]">
              It looks like we couldn't find any matching data. Try refining the filter options.
            </p>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default AssignmentLeaderboard;
